import { useMemo } from 'react';
import type { ScheduleEntry } from '@myapp/contract';
import { useAppointmentDetail } from '../../../entities/appointment';
import { useTaskDetail } from '../../../entities/task';
import {
  formatTimeHHmm,
  rawBoardMinutes,
  snapMinutes,
  toIsoDate,
} from '../../../shared/lib/schedule';
import type { PlanningSeed } from './draft';
import type { DispositionMode } from './useDispositionMode';

/** Where the entry being rescheduled currently sits, in the shapes the board and the bar need. */
export interface RescheduleSource {
  kind: 'appointment' | 'task';
  /** The owner's id — an appointment id or a task id, never `schedule.id`. */
  ownerId: number;
  /**
   * The entry's own `schedule.id` — what `PATCH /schedule/:entryId` is addressed by. A different
   * number from `ownerId`; null while the detail query is in flight.
   */
  entryId: number | null;
  /**
   * Any Ist-Zeitpunkt is set — the technician has started it or it is done. Such a block is drawn
   * from its actuals, so a plain slot move would not move it; only the form may rewrite those.
   */
  isLocked: boolean;
  /** The day the entry is on today — the day the board should open on. */
  dayIso: string;
  technicianName: string | null;
  /** `09:00` — the **work** start, the same number the planning bar shows for a new slot. */
  timeLabel: string;
  /**
   * Feeds `usePlanningDraft`, which applies it once per `key:dayIso` — this object is rebuilt on
   * every refetch of the detail query behind it, and re-placing the draft on that would undo a
   * placement the user had made by hand.
   */
  seed: PlanningSeed;
  /** What `PlanningBar` names an appointment by. Always null for a task. */
  workOrderId: string | null;
  isPending: boolean;
}

/**
 * The one owner of the entry being moved.
 *
 * Both the board (which hides its block, seeds the draft and scrolls to it) and `PlanningBar` (which
 * names it and offers the jump back) need the same derivation, so it happens here once rather than
 * twice with a chance to disagree. React Query dedupes the requests themselves.
 *
 * Returns `null` for a *placement* rather than a move: a new Termin, a brand-new Aufgabe, or an
 * Aufgabe that has no slot yet. Those have nothing to hide, nothing to seed and no previous slot to
 * name — which is what makes one code path serve both. An Aufgabe is told apart by its `schedule`,
 * so `planTask` and `rescheduleTask` can share `task-id`.
 */
export function useRescheduleSource(plan: DispositionMode): RescheduleSource | null {
  const appointmentId = plan.mode === 'schedule-appointment' ? plan.appointmentId : null;
  const taskId = plan.mode === 'schedule-task' ? plan.taskId : null;

  const appointment = useAppointmentDetail(appointmentId ?? undefined);
  const task = useTaskDetail(taskId ?? undefined);

  return useMemo(() => {
    if (appointmentId != null) {
      return build('appointment', appointmentId, appointment.data?.schedule ?? null, {
        technicianName: appointment.data?.technician?.name ?? null,
        workOrderId: appointment.data?.workOrderId ?? null,
        isPending: appointment.isPending,
      });
    }

    if (taskId != null) {
      const schedule = task.data?.schedule ?? null;
      // Loaded and unscheduled: this is a first placement, not a move.
      if (!task.isPending && schedule == null) return null;

      return build('task', taskId, schedule, {
        technicianName: task.data?.assignedTechnician?.fullName ?? null,
        workOrderId: null,
        isPending: task.isPending,
      });
    }

    return null;
  }, [appointmentId, taskId, appointment.data, appointment.isPending, task.data, task.isPending]);
}

function build(
  kind: RescheduleSource['kind'],
  ownerId: number,
  schedule: ScheduleEntry | null,
  rest: Pick<RescheduleSource, 'technicianName' | 'workOrderId' | 'isPending'>,
): RescheduleSource {
  const base = {
    kind,
    ownerId,
    entryId: schedule?.id ?? null,
    isLocked:
      schedule != null &&
      (schedule.travelStartedAt != null ||
        schedule.actualWorkStartsAt != null ||
        schedule.actualEndsAt != null ||
        schedule.departureEndedAt != null),
    ...rest,
  };

  // Still in flight. `dayIso: ''` matches no day, so the seed is never applied and the bar shows
  // its skeleton instead of a half-built line.
  if (!schedule) {
    return {
      ...base,
      dayIso: '',
      timeLabel: '',
      seed: { key: `${kind}:${ownerId}`, dayIso: '', state: EMPTY_STATE },
    };
  }

  // `startsAt` is the block start — travel already included — which is what a draft's `startMin`
  // means. Deriving it from `plannedWorkStartsAt` would place the draft one Anfahrt too late.
  const dayIso = toIsoDate(new Date(schedule.startsAt));

  // `actual ?? planned` per component, the same COALESCE the server drew `startsAt` with. Both
  // halves of the seed have to come from the same side or they describe no block that exists: a
  // completed Termin's `startsAt` is anchored on its Ist times, so pairing it with the *planned*
  // durations opened the draft at the right moment with the wrong length. It also decides what the
  // upsert form is handed on confirm — for a done entry that slot lands on the Ist-Zeiten, and
  // planned durations arriving there would overwrite what the Handwerker actually reported.
  const durations = {
    travel: schedule.actualTravelMinutes ?? schedule.plannedTravelMinutes,
    work: schedule.actualWorkMinutes ?? schedule.plannedWorkMinutes,
    departure: schedule.actualDepartureMinutes ?? schedule.plannedDepartureMinutes,
  };

  return {
    ...base,
    dayIso,
    timeLabel: formatTimeHHmm(schedule.actualWorkStartsAt ?? schedule.plannedWorkStartsAt),
    seed: {
      key: `${kind}:${ownerId}`,
      dayIso,
      state: {
        technicianId: schedule.technicianId,
        startMin: snapMinutes(rawBoardMinutes(schedule.startsAt, dayIso)),
        durations,
      },
    },
  };
}

/** Placeholder while a detail query is in flight — never applied, its `dayIso` matches no day. */
const EMPTY_STATE = {
  technicianId: 0,
  startMin: 0,
  durations: { travel: 0, work: 0, departure: 0 },
};
